import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronLeft, ChevronRight, LogOut, Moon, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Text } from '@/components/flup';
import { NAV, NavLink } from './nav';
import Images from '@/assets/images';
import SidebarPlanCard from './SidebarPlanCard';

/**
 * The Flup sidebar: a white sheet continuous with the content area, nav grouped
 * under uppercase headings, and a collapse that turns it into an icon rail
 * rather than hiding it.
 *
 * Collapsed is a real mode, not a narrower copy — the group headings become
 * their initial letter, which is what the reference does and what keeps the
 * rail's rhythm readable at 64px.
 */

interface SidebarProps {
  /** Expanded vs. the icon rail, on desktop. */
  collapsed: boolean;
  onToggleCollapsed: () => void;
  /** Whether the drawer is showing, on mobile. */
  mobileOpen: boolean;
  onCloseMobile: () => void;
  profileComplete: boolean;
  vendorName?: string;
  vendorRole?: string;
  vendorAvatar?: string;
  darkMode: boolean;
  onToggleDarkMode: () => void;
  onLogout: () => void;
}

const NavRow: React.FC<{
  item: NavLink;
  active: boolean;
  locked: boolean;
  collapsed: boolean;
  onNavigate: () => void;
}> = ({ item, active, locked, collapsed, onNavigate }) => {
  const Icon = item.icon;

  const body = (
    <>
      <Icon className="w-[18px] h-[18px] shrink-0" aria-hidden />
      {!collapsed ? (
        <>
          <Text variant="labelSemi" tone="inherit" as="span" className="truncate">
            {item.label}
          </Text>
          {item.comingSoon ? (
            <span className="ml-auto px-1.5 h-[18px] grid place-items-center rounded-flup-xs bg-flup-brand-soft shrink-0">
              <Text variant="micro" as="span" className="text-flup-brand-ink text-[10px]">
                Soon
              </Text>
            </span>
          ) : null}
        </>
      ) : null}
    </>
  );

  const classes = cn(
    'relative flex items-center gap-3 h-10 rounded-flup-sm transition-colors duration-150',
    collapsed ? 'w-10 justify-center px-0' : 'px-3',
    active
      ? 'bg-flup-green-soft text-flup-green-ink'
      : 'text-flup-ink-2 hover:bg-flup-surface-alt hover:text-flup-ink',
    locked && 'opacity-40 pointer-events-none',
  );

  if (locked || item.comingSoon) {
    return (
      <span className={classes} title={collapsed ? item.label : undefined} aria-disabled>
        {body}
      </span>
    );
  }

  return (
    <Link to={item.to} onClick={onNavigate} className={classes} title={collapsed ? item.label : undefined}>
      {/* A green edge marks the active row in the rail, where there is no
          room for the label to carry that job. */}
      {active && collapsed ? (
        <span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-pill bg-flup-green" aria-hidden />
      ) : null}
      {body}
    </Link>
  );
};

const Sidebar: React.FC<SidebarProps> = ({
  collapsed,
  onToggleCollapsed,
  mobileOpen,
  onCloseMobile,
  profileComplete,
  vendorName,
  vendorRole = 'Venue owner',
  vendorAvatar,
  darkMode,
  onToggleDarkMode,
  onLogout,
}) => {
  const location = useLocation();

  const isActive = (item: NavLink) =>
    item.matchPrefix
      ? location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
      : location.pathname === item.to;

  return (
    <>
      {/* Scrim, mobile only. */}
      {mobileOpen ? (
        <div
          className="fixed inset-0 z-40 bg-flup-ink/30 lg:hidden animate-fade-in"
          onClick={onCloseMobile}
          aria-hidden
        />
      ) : null}

      <aside
        className={cn(
          'fixed top-0 left-0 z-50 h-screen flex flex-col shrink-0',
          'bg-flup-surface border-r border-flup-hairline',
          'transition-[width,transform] duration-200',
          collapsed ? 'w-16' : 'w-64',
          mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
        )}
      >
        {/* Brand */}
        <div
          className={cn(
            'flex items-center h-16 shrink-0 border-b border-flup-hairline',
            collapsed ? 'justify-center px-2' : 'justify-between pl-4 pr-2',
          )}
        >
          {collapsed ? (
            <img src={Images.Logo_BYZ} alt="CO-BYZ" className="w-9 h-9 rounded-flup-sm object-contain" />
          ) : (
            <img src={Images.Logo_BYZ} alt="CO-BYZ" className="h-11 w-11 rounded-flup-sm object-contain" />
          )}

          <button
            type="button"
            onClick={onToggleCollapsed}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cn(
              'hidden lg:grid place-items-center w-7 h-7 rounded-flup-xs shrink-0',
              'text-flup-ink-3 hover:bg-flup-surface-alt hover:text-flup-ink transition-colors',
              collapsed && 'absolute -right-3 top-[18px] bg-flup-surface border border-flup-hairline shadow-flup-card',
            )}
          >
            {collapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
          </button>

          <button
            type="button"
            onClick={onCloseMobile}
            aria-label="Close menu"
            className="lg:hidden grid place-items-center w-8 h-8 rounded-flup-xs text-flup-ink-3 hover:bg-flup-surface-alt transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Nav */}
        <nav className={cn('flex-1 overflow-y-auto hide-scrollbar py-4', collapsed ? 'px-3' : 'px-3')}>
          {NAV.map((group) => (
            <div key={group.title} className="mb-5 last:mb-0">
              {collapsed ? (
                <Text
                  variant="eyebrow"
                  tone="tertiary"
                  as="p"
                  className="text-center mb-2"
                  title={group.title}
                >
                  {group.title.charAt(0)}
                </Text>
              ) : (
                <Text variant="eyebrow" tone="tertiary" as="p" className="px-3 mb-2">
                  {group.title}
                </Text>
              )}

              <div className={cn('space-y-0.5', collapsed && 'flex flex-col items-center')}>
                {group.items.map((item) => (
                  <NavRow
                    key={item.label}
                    item={item}
                    active={isActive(item)}
                    locked={Boolean(item.requiresProfile) && !profileComplete}
                    collapsed={collapsed}
                    onNavigate={onCloseMobile}
                  />
                ))}
              </div>
            </div>
          ))}
        </nav>

        {/* Plan for the venue in view + dark mode + identity + sign out */}
        <div className={cn('shrink-0 border-t border-flup-hairline py-3', collapsed ? 'px-3' : 'px-3')}>
          <SidebarPlanCard collapsed={collapsed} />

          <button
            type="button"
            onClick={onToggleDarkMode}
            role="switch"
            aria-checked={darkMode}
            aria-label="Dark mode"
            title={collapsed ? 'Dark mode' : undefined}
            className={cn(
              'flex items-center gap-3 h-10 w-full rounded-flup-sm text-flup-ink-2',
              'hover:bg-flup-surface-alt hover:text-flup-ink transition-colors',
              collapsed ? 'justify-center px-0' : 'px-3',
            )}
          >
            <Moon className="w-[18px] h-[18px] shrink-0" aria-hidden />
            {!collapsed ? (
              <>
                <Text variant="labelSemi" tone="inherit" as="span">
                  Dark mode
                </Text>
                <span
                  className={cn(
                    'ml-auto relative w-9 h-5 rounded-pill transition-colors shrink-0',
                    darkMode ? 'bg-flup-green' : 'bg-flup-sunken',
                  )}
                >
                  <span
                    className={cn(
                      // The thumb is white in both themes. It used to take
                      // --flup-surface, which is white on light but near-black
                      // on dark — so in dark mode the thumb inverted, and
                      // against the switched-off track (17% vs 11% lightness)
                      // there was nothing left to see.
                      'absolute top-0.5 left-0 w-4 h-4 rounded-pill bg-white shadow-sm transition-transform',
                      darkMode ? 'translate-x-[18px]' : 'translate-x-0.5',
                    )}
                  />
                </span>
              </>
            ) : null}
          </button>

          <div className={cn('flex items-center gap-2.5 mt-2 pt-3 border-t border-flup-hairline', collapsed && 'justify-center')}>
            {vendorAvatar ? (
              <img
                src={vendorAvatar}
                alt=""
                className="w-8 h-8 rounded-pill object-cover shrink-0 bg-flup-sunken"
              />
            ) : (
              <span className="grid place-items-center w-8 h-8 rounded-pill bg-flup-green-soft shrink-0">
                <Text variant="micro" as="span" className="text-flup-green-ink">
                  {(vendorName || 'V').charAt(0).toUpperCase()}
                </Text>
              </span>
            )}

            {!collapsed ? (
              <>
                <div className="min-w-0 flex-1">
                  <Text variant="labelSemi" as="p" lines={1}>
                    {vendorName || 'Vendor'}
                  </Text>
                  <Text variant="micro" tone="tertiary" as="p" lines={1}>
                    {vendorRole}
                  </Text>
                </div>
                <button
                  type="button"
                  onClick={onLogout}
                  aria-label="Log out"
                  className="grid place-items-center w-8 h-8 shrink-0 rounded-flup-xs text-flup-ink-3 hover:bg-flup-danger-soft hover:text-flup-danger transition-colors"
                >
                  <LogOut className="w-4 h-4" aria-hidden />
                </button>
              </>
            ) : null}
          </div>

          {collapsed ? (
            <button
              type="button"
              onClick={onLogout}
              aria-label="Log out"
              title="Log out"
              className="grid place-items-center w-10 h-10 mx-auto mt-2 rounded-flup-sm text-flup-ink-3 hover:bg-flup-danger-soft hover:text-flup-danger transition-colors"
            >
              <LogOut className="w-[18px] h-[18px]" aria-hidden />
            </button>
          ) : null}
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
